import { JobApplicationStatusLabel } from '@/components/JobApplicationStatusLabel';
import { JobButton } from '@/modules/JobButton';
import JobCard from '@/modules/JobCard';
import JobFavouriteButton from '@/modules/JobFavouriteButton';
import { IJobDetail } from '@/types/jobs';
import { generateJobDetailsURL } from '@/utils/jobListingHelper';
import { buildJobPostingJsonLd } from '@/utils/jobPostingSchema';
import { stringifyJsonLd } from '@/utils/jsonLd';
import { useAnalytics } from '@/utils/useAnalytics';
import React from 'react';

import styles from './JobListingJobCard.module.scss';

export type JobListingJobCardProps = {
  job: IJobDetail;
  isHighlighted?: boolean;
  // 1-based position of this card within the job listing, used for job_impression tracking.
  position?: number;
  // Emits a JobPosting JSON-LD script for this card. Only set this on indexable job listing
  // pages - "similar jobs" rendered on noindex pages (job detail, enquiry success) must not
  // publish structured data for jobs the page isn't actually describing (see A2P-4345 review).
  includeJsonLd?: boolean;
  // The page's server-computed origin (from getServerSideProps), threaded through so the
  // JobPosting JSON-LD's url field matches on the server render and the client hydration render.
  // Without this, buildJobPostingJsonLd falls back to getCanonicalOrigin(), which resolves
  // differently server-side (process.env.RECRUIT_WEB_URL) vs client-side
  // (window.location.origin) and throws a hydration mismatch whenever those two differ.
  origin?: string;
};

const JobListingJobCard = ({
  job,
  isHighlighted = false,
  position,
  includeJsonLd = false,
  origin,
}: JobListingJobCardProps) => {
  const track = useAnalytics();
  const subspcialityOrSpeciality = job.subspeciality ? job.subspeciality : job.speciality;
  const jobPostingJsonLd = includeJsonLd ? buildJobPostingJsonLd(job, origin) : undefined;

  return (
    <>
      {jobPostingJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: stringifyJsonLd(jobPostingJsonLd) }}
        />
      )}
      <JobCard
        topButtonElement={
          <>
            {!job.appliedStatus && (
              <JobFavouriteButton
                id={job.id}
                jobNumber={job.jobNumber}
                favourited={job.isSaved}
                style="icon"
              />
            )}
          </>
        }
        bottomButtonElement={
          <>
            {job.appliedStatus ? (
              <JobApplicationStatusLabel status={job.appliedStatus} />
            ) : (
              <JobButton
                buttonSize="medium"
                id={job.id}
                jobNumber={job.jobNumber}
                url={generateJobDetailsURL(job.jobNumber, job.grade, subspcialityOrSpeciality)}
                text="See details"
                isDetailsButton
                showIcon
                className={styles.detailsButton}
                trackingCallback={() => {
                  track('Job Details', {
                    'Details Origin': 'Job Card',
                    'Details Action': 'Button',
                    jobNumber: job.jobNumber,
                  });
                }}
              />
            )}
          </>
        }
        data={job}
        isJobSummary={true}
        isHighlighted={isHighlighted}
        enableGtmTracking
        position={position}
      />
    </>
  );
};

export default JobListingJobCard;
